/* @flow */
import { isEmpty, sortBy, filter } from 'lodash';
import * as React from 'react';
import { graphql } from 'react-apollo';
import { Container } from 'flux/utils';
import Modal from 'react-modal';
import BoxBody from 'components/BoxBody';
import ToastActions from 'actions/ToastActions';
import { promiseRelationship } from 'chat/utils/ChatPromises';
import AddButton from 'components/AddButton';
import AccordionTextWithIconItem from 'components/AccordionBodyItem/AccordionTextWithIconItem';
import AvatarWithTextItem from 'components/AvatarWithTextItem';
import AvatarWithTextItems from 'components/AvatarWithTextItems';
import Buttons from 'components/Buttons';
import ClipboardTrigger from 'components/ClipboardTrigger';
import Dialog from 'components/Dialog';
import Icon from 'components/Icon';
import LayerTrigger from 'components/LayerTrigger';
import Menu from 'components/Menu';
import ModalMenuItem from 'components/ModalMenuItem';
import Profile from 'components/Profile';
import Text from 'components/Text';
import Textarea from 'components/Textarea';
import TextWithIcon from 'components/TextWithIcon';
import Tooltip from 'components/Tooltip';
import TooltipTrigger from 'components/TooltipTrigger';
import RosterConstants from 'constants/RosterConstants';
import Group from 'entities/Group';
import Relationship from 'entities/Relationship';
import CopySubscriberDialog from 'groups/components/CopySubscriberDialog';
import GroupMemberFamily from 'groups/components/GroupMemberFamily';
import { format } from 'lib/phoneNumbers';
import CurrentUser from 'auth/CurrentUser';
import PhoneCallButton from 'phoneCalls/components/PhoneCallButton';
import SET_LANGUAGE_FOR_USER_MUTATION from 'users/components/UsersProfile/mutations/SetLanguageForUser.gql';
import StartChatButton from 'user/components/StartChatButton';
import UserActionsMenuButton from 'user/components/UserActionsMenuButton';
import UserPreferredLanguageForm from 'settings/components/UserPreferredLanguageForm';
import UsersAvatarWithText from 'users/components/UsersAvatarWithText';
import errorLogger from 'utils/errorLogger';
import mapGraphQLMemberToUserActionsMenuButtonUser from 'user/lib/mapGraphQLMemberToUserActionsMenuButtonUser';
import SettingsStore from 'stores/SettingsStore';
import { subtextForMember } from './utils';

import type { EntityType } from 'components/PeopleList/PeopleList';
import type { DeviceType, OrganizationRole } from 'queries/__generated__/globalGQLTypes';
import type { GroupProfileType } from 'groups/components/GroupProfile';
import type { DenialReason } from 'phoneCalls/components/PhoneCallModal';
import type { OrganizationMemberProfile_organizations_members_members } from './queries/__generated__/OrganizationMemberProfile';

import './UsersProfile.scss';
import { Link } from '../../../components/Link';
import Avatar from '../../../components/Avatar';

export type UserProfileUser = {};

type GroupAvatar = {
  +id: number,
  +color: ?string,
  +svgUrl: ?string,
};

type GroupInfo = {
  +id: number,
  +uuid: string,
  +className: string,
  +groupAvatar: ?GroupAvatar,
  +name: string,
  +hasRosterSource: boolean,
};

type AdminVisibleDeviceDetails = {
  +id: number,
  +endpoint: string,
  +type: DeviceType,
};

export type UserProfile = {
  id: number,
  uuid: string,
  name: string,
  firstName: string,
  lastName: string,
  signature?: string,
  note: string,
  preferences: {
    +preferred_language: string,
  },
  familySummary: {
    parents: $ReadOnlyArray<{
      +id: number,
      +uuid: string,
      +name: ?string,
      +color: ?string,
      +initials: ?string,
      +profilePictureUrl: ?string,
    }>,
    children: $ReadOnlyArray<{
      +id: number,
      +uuid: string,
      +name: ?string,
      +color: ?string,
      +initials: ?string,
      +profilePictureUrl: ?string,
    }>,
  },
  userInteractionPermissions: {
    +canAddChild: boolean,
    +canAddAsGroupOwner: boolean,
    +canAddParent: boolean,
    +canAssignRole: boolean,
    +canBlock: boolean,
    +canChangeRole: boolean,
    +canChat: boolean,
    +canCopyToAnotherGroup: boolean,
    +canRemoveFromGroup: boolean,
    +canReportToRemind: boolean,
    +canStartAChat: boolean,
    +canViewProfile: boolean,
    +canUnblock: boolean,
    +canPhone: boolean,
    +canPhoneDenialReason: ?string,
    +canSeeDevices: boolean,
    +canSetNote: boolean,
    +canRequestMessageHistory: boolean,
    +canGetMessageHistory: boolean,
    +canUpdatePreferredLanguage: boolean,
  },
  groupsOwned: $ReadOnlyArray<GroupInfo>,

  // Specific to org, but set to empty array for group contexts
  roles: OrganizationRole[],
  potentialRoles: $ReadOnlyArray<{
    +role: 'admin' | 'parent' | 'rce' | 'student' | 'teacher' | 'unknown',
    +display_role: string,
  }>,

  // Specific to org context
  adminVisibleDeviceDetails?: $ReadOnlyArray<AdminVisibleDeviceDetails>,
  groupsJoined?: $ReadOnlyArray<GroupInfo>,
  grade?: ?string,
  rosteredStatus?: boolean,

  // Specific to group context
  devices: *[],
  groupsInCommon?: $ReadOnlyArray<
    GroupInfo & {
      +isMember: boolean,
    }
  >,
};

export type Props = {
  member: UserProfile,
  onBackClicked?: () => void,
  onClose: () => void,
  onGroupClicked: (group: GroupProfileType) => void,
  onFamilyMemberClicked: (user: UserProfile, role: string) => void,
  onPreferredLanguageUpdated: () => Promise<OrganizationMemberProfile_organizations_members_members | void>,
  onNoteChange: (note: string) => Promise<any>,
  onUserChanged: () => void,
  trackEvent: (string, ?Object) => void,
  userActionsMenuButton: React.Element,
  entityUuid?: string,
  entityType?: EntityType,
  onHidePrivateNote?: () => void,
  showPrivateNote?: boolean,
};

type State = {
  addGroupModalRelationship: ?Relationship,
  note: string,
  noteChanged: boolean,
  showAddGroupModal: boolean,
  showPreferredLanguageModal: boolean,
  languages: { [key: string]: string },
};

const avatarWithTextProps = {
  subtle: true,
};

const visibleDeviceTypes = ['landline', 'sms', 'email'];

const withUserLanguageMutation = graphql(SET_LANGUAGE_FOR_USER_MUTATION);

const UserLanguageSelect = withUserLanguageMutation(UserPreferredLanguageForm);

class UsersProfile extends React.Component<Props, State> {
  static getStores() {
    return [SettingsStore];
  }

  static calculateState(prevState: State, props: Props): State {
    return {
      addGroupModalRelationship: null,
      note: props.member.note,
      noteChanged: false,
      showAddGroupModal: false,
      showPreferredLanguageModal: false,
      languages: SettingsStore.getSetting('languages', 'secondary'),
    };
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      addGroupModalRelationship: null,
      note: props.member.note,
      noteChanged: false,
      showAddGroupModal: false,
      showPreferredLanguageModal: false,
      languages: {},
    };
  }

  closeModal = () => {
    this.setState({
      addGroupModalRelationship: null,
      showAddGroupModal: false,
    });
  };

  handleEmailCopy = () => {
    ToastActions.showSuccessToast('Email copied to clipboard');
    this.props.trackEvent('user_profile.device.copied');
  };

  hasNewTranslationsEnabled = () => {
    return (
      CurrentUser.isSignedIn() &&
      CurrentUser.getUser() &&
      CurrentUser.getUser().hasFeature('2020.dec.preferred_language_translation_v0')
    );
  };

  preferredLanguageSelect = (canUpdatePreferredLanguage?: boolean) => {
    const { entityUuid, entityType, onPreferredLanguageUpdated } = this.props;
    const { uuid } = this.props.member;
    const preferredLanguage = this.props.member?.preferences?.preferred_language;

    const dialog = canUpdatePreferredLanguage ? (
      <React.Fragment>
        <Modal isOpen={this.state.showPreferredLanguageModal}>
          <Dialog
            title="Preferred language"
            size="small"
            onRequestHide={() => this.setState({ showPreferredLanguageModal: false })}>
            <BoxBody divided size="small">
              <UserLanguageSelect
                userUuid={uuid}
                targetUuid={entityUuid}
                targetType={entityType}
                preferredLanguage={preferredLanguage}
                onPreferredLanguageUpdated={() => {
                  this.setState({ showPreferredLanguageModal: false });
                  onPreferredLanguageUpdated();
                }}
                hideHeader
                full
                onRequestHide={() => this.setState({ showPreferredLanguageModal: false })}
              />
            </BoxBody>
          </Dialog>
        </Modal>
        <Link onClick={() => this.setState({ showPreferredLanguageModal: true })}>Edit</Link>
      </React.Fragment>
    ) : (
      <span />
    );

    const languageModal = (
      <Dialog title="Preferred language" size="small">
        <BoxBody divided size="small">
          <UserLanguageSelect
            userUuid={uuid}
            targetUuid={entityUuid}
            targetType={entityType}
            preferredLanguage={preferredLanguage}
            onPreferredLanguageUpdated={onPreferredLanguageUpdated}
            hideHeader
            full
          />
        </BoxBody>
      </Dialog>
    );

    return this.hasProfileRefresh() ? (
      <ModalMenuItem
        modal={languageModal}
        disabled={!canUpdatePreferredLanguage}
        key="edit-language">
        <TextWithIcon
          icon="globe-stroked"
          color="chalkboard"
          iconColor="chalkboard"
          iconSize="medium"
          size="xsmall"
          noHover>
          Edit preferred language
        </TextWithIcon>
      </ModalMenuItem>
    ) : (
      <AvatarWithTextItems>
        <AvatarWithTextItem
          {...avatarWithTextProps}
          avatar={<Avatar iconName={'globe-stroked'} />}
          key={`lang_select${this.props.member.id}`}
          subtext={dialog}
          title={(preferredLanguage && this.state.languages[preferredLanguage]) ?? 'No preference'}
          data-test="lang_select"
        />
      </AvatarWithTextItems>
    );
  };

  adminDevicesList = (devices: AdminVisibleDeviceDetails[]) => {
    if (isEmpty(devices)) {
      return null;
    }

    const devicesSorted = sortBy(devices, item => (item.type === 'sms' ? 0 : 1));

    return (
      <AvatarWithTextItems>
        {devicesSorted.map(item => {
          const { endpoint, id, type } = item;

          const isEmail = type === 'email';

          const avatarWithTextItem = (
            <AccordionTextWithIconItem
              {...avatarWithTextProps}
              icon={isEmail ? 'mail' : 'phone'}
              key={id}
              title={isEmail ? endpoint : format(endpoint)}
              data-test="device-item"
            />
          );

          if (isEmail) {
            return (
              <TooltipTrigger
                placement="top"
                key={`tooltip_trigger${id}`}
                tooltip={
                  <Tooltip>
                    Click to copy <Text wrap="force">{endpoint}</Text>
                  </Tooltip>
                }>
                <div>
                  <ClipboardTrigger text={endpoint} onCopy={this.handleEmailCopy}>
                    {avatarWithTextItem}
                  </ClipboardTrigger>
                </div>
              </TooltipTrigger>
            );
          }

          return avatarWithTextItem;
        })}
      </AvatarWithTextItems>
    );
  };

  groupsList = (groups: $ReadOnlyArray<GroupInfo>, groupType: 'owned' | 'joined') => {
    const { onGroupClicked, onUserChanged, member } = this.props;
    const {
      userInteractionPermissions: { canRemoveFromGroup },
    } = member;

    if (isEmpty(groups)) {
      return null;
    }

    return (
      <AvatarWithTextItems>
        {groups.map(item => {
          const groupEntity = ((Group.fromJSON(item): any): Group);
          return (
            <AvatarWithTextItem
              {...avatarWithTextProps}
              key={item.uuid}
              action={
                groupType === 'joined' ? (
                  <UserActionsMenuButton
                    buttonSize="xlarge"
                    level="icon"
                    glyphSize="inherit"
                    groupToAdmin={groupEntity}
                    isAdminView
                    showRemoveMember
                    disableRemoveMember={!canRemoveFromGroup}
                    onUserRemoved={onUserChanged}
                    user={mapGraphQLMemberToUserActionsMenuButtonUser(member)}
                    tooltip="More actions"
                    tooltipPlacement="top"
                  />
                ) : null
              }
              avatarSize={this.hasProfileRefresh() ? 'small' : 'xsmall'}
              group={groupEntity}
              hideCode
              // $FlowFixMe - GroupsProfile uses graphql types, so we're missing __typename all over
              onClick={() => onGroupClicked(item)}
            />
          );
        })}
      </AvatarWithTextItems>
    );
  };

  handleAddGroup = () => {
    promiseRelationship(this.props.member.id)
      .then(relationship => {
        this.setState({
          addGroupModalRelationship: relationship,
          showAddGroupModal: true,
        });
      })
      .catch(errorLogger('PropleListUserPropfile handleAddGroup failed'));
  };

  handleSectionVisibilityChange = (uuidsOfExpandedSections: string[]) => {
    const { member, trackEvent } = this.props;

    if (uuidsOfExpandedSections.includes('Contact information')) {
      trackEvent('user_profile.devices.view', {
        affected_user_uuid: member.uuid,
      });
    }
  };

  handleNoteBlur = () => {
    const { onNoteChange, onHidePrivateNote } = this.props;
    const { note, noteChanged } = this.state;

    if (!noteChanged) {
      if (onHidePrivateNote) {
        onHidePrivateNote();
      }
      return;
    }

    onNoteChange(note)
      .then(() => {
        if (onHidePrivateNote) {
          onHidePrivateNote();
        }
      })
      .catch(() => {
        console.error('unable to hide private note');
      });

    ToastActions.showSuccessToast('Note updated');
    this.setState({ noteChanged: false });
  };

  handleNoteChange = (event: SyntheticEvent<HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;

    this.setState(prevState => ({
      note: value,
      noteChanged: prevState.note !== value,
    }));
  };

  adminVisibleDevicesForUser = (user: UserProfile): Array<AdminVisibleDeviceDetails> => {
    if (user.adminVisibleDeviceDetails) {
      return filter(user.adminVisibleDeviceDetails, d => visibleDeviceTypes.includes(d.type));
    }
    return [];
  };

  getSections = () => {
    const { onFamilyMemberClicked, onUserChanged, member, trackEvent } = this.props;
    const {
      roles = [],
      familySummary,
      groupsJoined,
      groupsOwned,
      groupsInCommon,
      userInteractionPermissions,
    } = member;
    const {
      canAddChild,
      canAddParent,
      canCopyToAnotherGroup,
      canSeeDevices,
    } = userInteractionPermissions;
    const { parents, children } = familySummary;
    const familyMemberCount = parents.length + children.length;

    const sections = [
      {
        title: 'Family members',
        children: (
          <GroupMemberFamily
            key={member.id}
            canAddChild={canAddChild}
            canAddParent={canAddParent}
            familySummary={familySummary}
            orientation="vertical"
            userId={member.id}
            onFamilyMemberClick={onFamilyMemberClicked}
            onAddFamilyMember={() => {
              trackEvent('user_profile.family_members.added');
              onUserChanged();
            }}
          />
        ),
        count: familyMemberCount,
        defaultOpen: !roles.includes('teacher') || !roles.includes('admin'),
      },
      {
        title: 'Classes owned',
        children: this.groupsList(groupsOwned, 'owned'),
        count: groupsOwned.length,
        defaultOpen: roles.includes('teacher'),
      },
    ];

    if (canSeeDevices && member.adminVisibleDeviceDetails) {
      const devices = this.adminVisibleDevicesForUser(member);

      sections.unshift({
        title: 'Contact information',
        children: this.adminDevicesList(devices),
        count: devices.length,
      });
    }

    if (this.hasNewTranslationsEnabled() && !this.hasProfileRefresh()) {
      sections.unshift({
        title: 'Preferred language',
        children: this.preferredLanguageSelect(
          userInteractionPermissions.canUpdatePreferredLanguage
        ),
        count: 1,
        defaultOpen: true,
      });
    }

    if (groupsInCommon) {
      sections.push({
        title: 'Classes shared',
        children: this.groupsList(groupsInCommon, 'joined'),
        count: groupsInCommon.length,
      });
    }

    if (groupsJoined) {
      sections.push({
        title: 'Classes joined',
        children: (
          <React.Fragment>
            {this.groupsList(groupsJoined, 'joined')}
            {canCopyToAnotherGroup && (
              <AddButton
                className={this.hasProfileRefresh() ? 'AddButton' : ''}
                listFooter
                size="large"
                onClick={this.handleAddGroup}>
                Add to another class
              </AddButton>
            )}
          </React.Fragment>
        ),
        count: groupsJoined.length,
      });
    }

    return sections;
  };

  hasProfileRefresh = () => {
    const currentUser = CurrentUser.getUser();
    return currentUser && currentUser.hasFeature('profile_refresh.web.5-5');
  };

  renderActionButtons = () => {
    const { member, userActionsMenuButton } = this.props;
    const { userInteractionPermissions } = member;

    const { canChat, canPhone, canPhoneDenialReason } = userInteractionPermissions;

    const denialReason = canPhone
      ? undefined
      : ((canPhoneDenialReason || 'feature_not_available': any): DenialReason);

    return (
      <Buttons fullButtonIndex={0}>
        <StartChatButton
          disabled={!canChat}
          size="xsmall"
          subscriber={member.id}
          text={this.hasProfileRefresh() ? 'Message' : 'Send message'}
        />
        <PhoneCallButton
          calleeName={member.name || ''}
          calleeUuid={member.uuid}
          glyphSize="inherit"
          size="xsmall"
          denialReason={denialReason}
        />
        {userActionsMenuButton}
      </Buttons>
    );
  };

  renderNoteField = () => {
    const { member, showPrivateNote } = this.props;
    const { note } = this.state;
    const { canSetNote } = member.userInteractionPermissions;

    const privateNoteVisibility = showPrivateNote || !!member.note;

    return this.hasProfileRefresh() ? (
      <React.Fragment>
        {privateNoteVisibility && (
          <Textarea
            autoFocus={!note}
            disabled={!canSetNote}
            placeholder="Add a private note"
            rows={1}
            value={note}
            onBlur={this.handleNoteBlur}
            onChange={this.handleNoteChange}
            data-test="note-field"
          />
        )}
      </React.Fragment>
    ) : (
      <Textarea
        disabled={!canSetNote}
        placeholder="Add a private note"
        rows={2}
        value={note}
        onBlur={this.handleNoteBlur}
        onChange={this.handleNoteChange}
        data-test="note-field"
      />
    );
  };

  renderRosteredIcon() {
    const { member } = this.props;
    const { rosteredStatus } = member;

    // For the class level people list, we don't load or show rostering info
    if (rosteredStatus === null || rosteredStatus === undefined) {
      return null;
    }

    return (
      <TooltipTrigger
        placement="top"
        tooltip={
          <Tooltip>
            {rosteredStatus ? RosterConstants.rosteredLabel : RosterConstants.notRosteredLabel}
          </Tooltip>
        }>
        <span>
          <Icon
            color={
              rosteredStatus ? RosterConstants.rosteredColor : RosterConstants.notRosteredColor
            }
            name={rosteredStatus ? RosterConstants.rosteredIcon : RosterConstants.notRosteredIcon}
            size="xlarge"
          />
        </span>
      </TooltipTrigger>
    );
  }

  subtextForHeader() {
    const { member } = this.props;
    const { signature } = member;
    const langOpts = SettingsStore.getSetting('languages');
    const preferredLanguage = member?.preferences?.preferred_language;
    let subtext: string = '';

    if (signature) {
      subtext += signature;
    }
    if (preferredLanguage) {
      if (signature) {
        subtext += ' • ';
      }
      subtext += langOpts.secondary[preferredLanguage];
    }
    return subtext;
  }

  renderHeaderMenu = (canUpdatePreferredLanguage?: boolean) => {
    const headerMenuSections = [];

    if (this.hasNewTranslationsEnabled()) {
      headerMenuSections.push(this.preferredLanguageSelect(canUpdatePreferredLanguage));
    }

    return <Menu rowHover>{headerMenuSections}</Menu>;
  };

  renderHeader = (canUpdatePreferredLanguage?: boolean) => {
    const { member } = this.props;

    return (
      <LayerTrigger
        layer={this.renderHeaderMenu(canUpdatePreferredLanguage)}
        placement="bottom"
        alignment="right"
        trigger="click"
        activeClassName="Profile--EditNameMenu">
        <div className="Profile--Avatar">
          <UsersAvatarWithText
            avatarSize="medium"
            center={false}
            icon={this.renderRosteredIcon()}
            level="profile"
            subtext={this.subtextForHeader()}
            subtle
            textSize="medium"
            user={member}
          />
          <Icon
            className="Profile--EditMenuButton"
            name="vertical-dots"
            size="large"
            color="chalkboard"
          />
        </div>
      </LayerTrigger>
    );
  };

  render() {
    const { addGroupModalRelationship, showAddGroupModal } = this.state;
    const { onBackClicked, onClose, onUserChanged, member } = this.props;

    return (
      <React.Fragment>
        <Profile
          actions={this.renderActionButtons()}
          errorBanner={null}
          loadingIndicator={null}
          noteField={this.renderNoteField()}
          sections={this.getSections()}
          onBackClicked={onBackClicked}
          onChange={this.handleSectionVisibilityChange}
          onClose={onClose}
          header={
            this.hasProfileRefresh() ? (
              this.renderHeader(member.userInteractionPermissions.canUpdatePreferredLanguage)
            ) : (
              <UsersAvatarWithText
                avatarSize="xxlarge"
                icon={this.renderRosteredIcon()}
                level="primary"
                subtext={subtextForMember(member)}
                user={member}
              />
            )
          }
          className="UsersProfile"
        />
        {addGroupModalRelationship && (
          <Modal isOpen={showAddGroupModal}>
            <CopySubscriberDialog
              relationships={[addGroupModalRelationship]}
              onRequestHide={this.closeModal}
              onSubscribe={onUserChanged}
            />
          </Modal>
        )}
      </React.Fragment>
    );
  }
}

export { UsersProfile };
export default Container.create<Props, State>(UsersProfile, { withProps: true, pure: false });
